import { API_URLS } from "../Constants/api.constants";
import api from "./api";
import { errorHandling } from "./error.handling";
import { storage } from "./storage.service";
import { storageKeys } from "../Constants/storage.key";
import Cookies from "js-cookie";
import { licenseService } from "./license.service";

const basePath = `${API_URLS.AUTH}/v3/Auth`;
function currentHostname() {
  if (typeof window === "undefined") return "default";
  const h = window.location.hostname;
  return (h === "localhost" || h === "127.0.0.1")
    ? (process.env.REACT_APP_FIXED_DOMAIN ? new URL(process.env.REACT_APP_FIXED_DOMAIN).hostname : "localhost")
    : h;
}

const DEVICE_COOKIE_KEY = () => `movyx-device-id-${currentHostname()}`;
const LICENSE_COOKIE_KEY = () => `movyx-companyId-${currentHostname()}`;
const DEVICE_STORAGE_KEY = () => `ofv2-device-id-${currentHostname()}`;

export const authService = {
  auth,
  fetchAuth,
  authenticated,
  recoverPass,
  logoff,
  key,
  resetPass,
  authorizedLogin,
  refreshToken,
  getCurrentPermissions
};

async function auth(payload) {
  try {
    const normalizedPayload = await normalizeAuthPayload(payload);
    const response = await api.post(`${basePath}/login`, normalizedPayload);

    const treated = errorHandling.handleResponse(response, "authService.auth");
    
    if (treated) {
      if (treated.token) {
        // Salvar tanto o token quanto o refreshToken se disponível
        const tokenData = {
          token: treated.token,
          tokenExpiresAt: treated.tokenExpiresAt,
          refreshToken: treated.refreshToken,
          refreshTokenExpiresAt: treated.refreshTokenExpiresAt
        };
        
        storage.save(storageKeys.access_and_token, tokenData);
        return true;
      }
    }
    return false;
  } catch (error) {
    errorHandling.cathError(error, "authService.auth");
    return false;
  }
}

async function fetchAuth() {
  try {
    const response = await api.get(`${API_URLS.AUTH}/v3/auth`);

    const treated = errorHandling.handleResponse(response, "authService.fetchAuth");
    if (treated) {
      return treated;
    }

    return null;
  } catch (error) {
    errorHandling.cathError(error, "authService.fetchAuth");
    return null;
  }
}

async function normalizeAuthPayload(payload = {}) {
  const resolvedLicenseId =
    payload?.LicenseId ?? payload?.licenseId ?? (await resolveLicenseId());

  return {
    login: payload?.login ?? payload?.Login ?? "",
    pass: payload?.pass ?? payload?.Pass ?? "",
    userId: payload?.userId ?? payload?.UserId ?? 0,
    userLogId: payload?.userLogId ?? payload?.UserLogId ?? 0,
    clientId: payload?.clientId ?? payload?.ClientId ?? 0,
    ...(resolvedLicenseId !== undefined &&
    resolvedLicenseId !== null &&
    resolvedLicenseId !== ""
      ? { licenseId: resolvedLicenseId }
      : {}),
  };
}

async function authorizedLogin(payload) {
  try {
    const normalizedPayload = await normalizeAuthorizedLoginPayload(payload);

    const response = await api.post(
      `${basePath}/users/authorized/login`,
      normalizedPayload
    );

    const treated = errorHandling.handleResponse(response, "authService.authorizedLogin");
    if (treated) {
      return treated
    }
    return null;
  } catch (error) {
    errorHandling.cathError(error, "authService.authorizedLogin");
    return null;
  }
}

async function normalizeAuthorizedLoginPayload(payload = {}) {
  const resolvedLicenseId =
    payload?.LicenseId ?? payload?.licenseId ?? (await resolveLicenseId());

  const providedDeviceId = payload?.DeviceId ?? payload?.deviceId;
  const resolvedDeviceId =
    typeof providedDeviceId === "string" && providedDeviceId.trim().length > 0
      ? providedDeviceId.trim()
      : getOrCreateDeviceId();

  if (resolvedDeviceId) {
    persistDeviceId(resolvedDeviceId);
  }

  return {
    LicenseId:
      resolvedLicenseId === undefined || resolvedLicenseId === null
        ? ""
        : String(resolvedLicenseId),
    Login: payload?.Login ?? payload?.login ?? "",
    Pass: payload?.Pass ?? payload?.pass ?? "",
    DeviceId: resolvedDeviceId,
  };
}

function resolveLicenseId() {
  const userSettings = storage.get(storageKeys.settings);

  const licenseIdFromStorage =
    userSettings?.licenseeId ??
    userSettings?.LicenseeId ??
    userSettings?.clientLicensedId ??
    userSettings?.licenseId ??
    userSettings?.LicenseId ??
    userSettings?.licensedId ??
    userSettings?.LicensedId;

  if (licenseIdFromStorage !== undefined && licenseIdFromStorage !== null && licenseIdFromStorage !== "") {
    return licenseIdFromStorage;
  }

  const licenseIdFromCookie = Cookies.get(LICENSE_COOKIE_KEY());
  if (licenseIdFromCookie) {
    return licenseIdFromCookie;
  }

  const domain = resolveDomain();
  if (!domain) {
    return "";
  }

  return licenseService
    .fetchLicensesByDomain(domain)
    .then((licensesByDomain) => {
      const firstLicense = Array.isArray(licensesByDomain)
        ? licensesByDomain[0]
        : null;

      const resolvedFromDomain =
        firstLicense?.licensedId ??
        firstLicense?.LicensedId ??
        firstLicense?.licenseId ??
        firstLicense?.LicenseId;

      if (
        resolvedFromDomain !== undefined &&
        resolvedFromDomain !== null &&
        resolvedFromDomain !== ""
      ) {
        Cookies.set(LICENSE_COOKIE_KEY(), String(resolvedFromDomain), {
          expires: 30,
        });
        return resolvedFromDomain;
      }

      return "";
    })
    .catch(() => "");
}

function resolveDomain() {
  if (typeof window === "undefined") return "";

  if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") {
    return process.env.REACT_APP_FIXED_DOMAIN || "";
  }

  return `${window.location.origin}/`;
}

function getOrCreateDeviceId() {
  const persistedStorageDeviceId = storage.get(DEVICE_STORAGE_KEY());
  if (typeof persistedStorageDeviceId === "string" && persistedStorageDeviceId.trim().length > 0) {
    return persistedStorageDeviceId.trim();
  }

  const persistedCookieDeviceId = Cookies.get(DEVICE_COOKIE_KEY());
  if (persistedCookieDeviceId && persistedCookieDeviceId.trim().length > 0) {
    const normalizedCookieDeviceId = persistedCookieDeviceId.trim();
    storage.save(DEVICE_STORAGE_KEY(), normalizedCookieDeviceId);
    return normalizedCookieDeviceId;
  }

  const generatedDeviceId = generateDeviceId();
  persistDeviceId(generatedDeviceId);
  return generatedDeviceId;
}

function persistDeviceId(deviceId) {
  if (!deviceId) return;

  storage.save(DEVICE_STORAGE_KEY(), deviceId);
  Cookies.set(DEVICE_COOKIE_KEY(), deviceId, { expires: 365 });
}

function generateDeviceId() {
  const hasRandomUUID =
    typeof window !== "undefined" &&
    window?.crypto &&
    typeof window.crypto.randomUUID === "function";

  if (hasRandomUUID) {
    return window.crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

async function key(payload) {
  try {
    const response = await api.post(`${basePath}/login/key`, payload);

    const treated = errorHandling.handleResponse(response, "authService.key");
    if (treated){
      return treated;
    }
    
  } catch (error) {
    errorHandling.cathError(error, "authService.auth");
    return false;
  }
}

function authenticated() {
  try {
    const accessToken = storage.get(storageKeys.access_and_token);
    if (accessToken.token) return true;
  } catch {
    return false;
  }

  return false;
}

function logoff() {
  console.clear();
  try { storage.remove(storageKeys.access_and_token); } catch {}
  try { storage.remove(storageKeys.settings); } catch {}
  try { storage.remove(storageKeys.user_data); } catch {}
  try { localStorage.clear(); } catch {}
  try { sessionStorage.clear(); } catch {}

  try {
    const cookies = document.cookie ? document.cookie.split(";") : [];
    for (const cookie of cookies) {
      const cookieName = cookie.split("=")[0].trim();
      document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    }
  } catch {}
}

async function recoverPass(payload) {
  try {
    const response = await api.post(`${basePath}/password/forgot`, payload);

    const treated = errorHandling.handleResponse(
      response,
      "authService.recoverPass"
    );
    if (treated) {
      if (treated.message) return treated.message;
      return treated
    }
  } catch {
    return null;
  }

  return null;
}

async function resetPass(payload) {
  try {
    const response = await api.post(`${basePath}/password/reset`, payload);

    const treated = errorHandling.handleResponse(
      response,
      "authService.resetPass"
    );
    if (treated) {
      if (treated.message) return treated.message;
      return treated
    }
  } catch {
    return null;
  }

  return null;
}

async function refreshToken() {
  try {
    const tokenData = storage.get(storageKeys.access_and_token);
    
    if (!tokenData?.token || !tokenData?.refreshToken) {
      throw new Error('No tokens available');
    }

    const payload = {
      token: tokenData.token,
      refreshToken: tokenData.refreshToken
    };

    const response = await api.post(`${API_URLS.AUTH}/v3/auth/refresh-token`, payload);

    const treated = errorHandling.handleResponse(response, "authService.refreshToken");

    if (treated) {
      const newTokenData = {
        token: treated.token || treated.Token,
        tokenExpiresAt: treated.tokenExpiresAt || treated.TokenExpiresAt,
        refreshToken: treated.refreshToken || treated.RefreshToken,
        refreshTokenExpiresAt: treated.refreshTokenExpiresAt || treated.RefreshTokenExpiresAt
      };
      
      storage.save(storageKeys.access_and_token, newTokenData);
      return newTokenData;
    }
    return null;
  } catch (_error) {
    // Intentionally avoiding logging token-bearing errors to console
    return null;
  }
}

function getCurrentPermissions() {
  const userSession = storage.get(storageKeys.settings);
  if (!userSession) {
    return { canEdit: true, canExecuteCommand: true };
  }

  return {
    canEdit: userSession.editPermission === "S" || userSession.EditPermission === "S",
    canExecuteCommand:
      userSession.commandPermission === "S" || userSession.CommandPermission === "S",
  };
}
